import React, {useMemo, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {FormattedMessage, useIntl} from 'react-intl';

import {useAccessToken} from '@salesforce/commerce-sdk-react';
import {
  Box, FormLabel, HStack, IconButton, Input, Text, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {ArrowRightIcon, CheckIcon} from '@amblique/king-living/app/components/icons';
import {getSiteConfig} from '@amblique/king-living/app/constants/site-config';
import {useCrm} from '@amblique/king-living/app/contexts/crm-context';
import {useFooterBySiteId} from '@amblique/king-living/app/contentful/hooks/use-footer';
import {useGlobalConfig} from '@amblique/king-living/app/contexts/global-config-context';
import SocialIcons from '@salesforce/retail-react-app/app/components/social-icons';

import {fetchWithTimeout} from '@amblique/king-living/app/utils/fetch-with-timeout';
import {trackSignUp} from '@amblique/king-living/app/analytics/ga4';
import {
  FIELD_LIMITS,
  sanitizeNameNumbers,
  validateEmail,
  validateName,
} from '@amblique/king-living/app/utils/validation';
import {FieldLabelContent, appendRequiredAsterisk} from '@amblique/king-living/app/components/field/field-label-content';
import {useRecaptcha} from '@amblique/king-living/app/hooks/use-recaptcha';
import {
  RecaptchaErrorMessage,
  withRecaptcha,
} from '@amblique/king-living/app/components/recaptcha';
import {FieldValidationStatus} from '@amblique/king-living/app/components/field/field-validation-status';
import {RECAPTCHA_ACTIONS} from '@amblique/king-living/app/constants/recaptcha';

const NEWSLETTER_FIELD_IDS = {
  email: 'newsletter-email',
  firstName: 'newsletter-first-name',
  lastName: 'newsletter-last-name',
};

const NewsletterBase = () => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('Newsletter', {});
  const {site} = useMultiSite();
  const siteConfig = getSiteConfig(site?.id);

  const {data: customer} = useCurrentCustomer();
  const {getTokenWhenReady} = useAccessToken();
  const {updateCrmProfile} = useCrm();
  const {signUpHeader} = useGlobalConfig();

  // reCAPTCHA v3 token verification (GR-004/005/012) — footer subscription scope.
  const {verify: verifyRecaptcha} = useRecaptcha();

  const {data: footerData} = useFooterBySiteId(site?.id, {
    variant: 'withColumns',
  });

  const socialLinks = useMemo(
    () => footerData?.socialLinksCollection?.items ?? [],
    [footerData]
  );

  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isSuccess: false,
    isError: false,
    subscribedEmail: '',
    errorMessage: null,
  });

  const {
    control,
    handleSubmit,
    formState: {errors, isSubmitting},
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const firstNameLabel = intl.formatMessage({
    id: 'newsletter.placeholder.first_name',
    defaultMessage: 'First name',
  });
  const lastNameLabel = intl.formatMessage({
    id: 'newsletter.placeholder.last_name',
    defaultMessage: 'Last name',
  });
  const emailLabel = intl.formatMessage({
    id: 'newsletter.label.email',
    defaultMessage: 'Email address',
  });
  const firstNameKeyDownRef = useRef(() => {});
  const lastNameKeyDownRef = useRef(() => {});
  const emailKeyDownRef = useRef(() => {});

  const onSubmit = async (data) => {
    if (!customer?.isRegistered) {
      void trackSignUp({
        customer: {customerId: customer?.customerId, email: data.email},
        method: 'newsletter sign-up',
      });
    }

    try {
      // GR-012: verify reCAPTCHA before subscribing. On a failed score the
      // request is blocked and the GR-012 message is shown beneath the form.
      // Fails open when reCAPTCHA is disabled/unconfigured (GR-001).
      const recaptcha = await verifyRecaptcha(RECAPTCHA_ACTIONS.NEWSLETTER);

      if (!recaptcha.success) {
        setSubscriptionStatus({
          isSuccess: false,
          isError: true,
          subscribedEmail: '',
          errorMessage: recaptcha.message,
        });

        return;
      }

      let result;

      if (customer?.isRegistered) {
        // Logged-in user: update preferences via authenticated endpoint (CRM-018)
        const accessToken = await getTokenWhenReady();

        const response = await fetchWithTimeout('/api/crm/preferences', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({emailMarketing: true}),
        });

        result = await response.json();
      } else {
        // Guest user: create/update CRM record via preferences endpoint (no auth required when email provided)
        const response = await fetchWithTimeout('/api/crm/preferences', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            emailMarketing: true,
            countryCode: siteConfig?.countryCode || 'AU',
          }),
        });

        result = await response.json();
      }

      if (result.ok) {
        setSubscriptionStatus({
          isSuccess: true,
          isError: false,
          subscribedEmail: data.email,
          errorMessage: null,
        });

        updateCrmProfile({emailOptOut: false});
      } else {
        setSubscriptionStatus({
          isSuccess: false,
          isError: true,
          subscribedEmail: '',
          errorMessage: null,
        });
      }
    } catch (error) {
      console.error('[Newsletter] Subscription error:', error);
      setSubscriptionStatus({
        isSuccess: false,
        isError: true,
        subscribedEmail: '',
        errorMessage: null,
      });
    }
  };

  const isSuccess = subscriptionStatus.isSuccess;
  const subscribedEmail = subscriptionStatus.subscribedEmail;

  return (
    <Box className="newsletter-container" sx={styles.container}>
      <VStack align="stretch" {...styles.wrapper}>
        <VStack align="stretch" spacing={3}>
          <Text className="newsletter-heading" sx={styles.heading}>
            {signUpHeader || 'Stay in the know'}
          </Text>
          <Text className="newsletter-subtitle" sx={styles.subtitle}>
            <FormattedMessage
              defaultMessage="Sign up to King Living to hear about new collections, offers and inspiration"
              id="newsletter.subtitle.sign_up"
            />
          </Text>

          <Box as="form" className="newsletter-form" noValidate onSubmit={handleSubmit(onSubmit)}>
            {!isSuccess && !customer?.isRegistered && (
              <Box sx={styles.nameFields}>
                <Box sx={styles.nameFieldColumn}>
                  <Box sx={styles.nameFieldWrapper}>
                    <FormLabel className="sr-only" htmlFor={NEWSLETTER_FIELD_IDS.firstName}>
                      <FieldLabelContent label={firstNameLabel} required />
                    </FormLabel>
                    <Controller
                      control={control}
                      name="firstName"
                      rules={{
                        required: intl.formatMessage({
                          id: 'newsletter.validation.first_name_required',
                          defaultMessage: 'First name is required',
                        }),
                        validate: (value) => validateName(value, {
                          maxLength: FIELD_LIMITS.firstName,
                          fieldName: firstNameLabel,
                          formatMessage: intl.formatMessage,
                        }),
                      }}
                      render={({field: {onBlur, onChange, ...field}, fieldState}) => (
                        <>
                          <Input
                            {...field}
                            id={NEWSLETTER_FIELD_IDS.firstName}
                            maxLength={FIELD_LIMITS.firstName}
                            placeholder={appendRequiredAsterisk(firstNameLabel)}
                            sx={styles.input}
                            onBlur={onBlur}
                            onChange={(event) => onChange(sanitizeNameNumbers(event.target.value))}
                            onKeyDown={() => firstNameKeyDownRef.current()}
                          />
                          <FieldValidationStatus
                            enabled
                            errorMessage={fieldState.error?.message}
                            formatMessage={intl.formatMessage}
                            id={`${NEWSLETTER_FIELD_IDS.firstName}-status`}
                            invalid={!!errors.firstName}
                            keyDownRef={firstNameKeyDownRef}
                            label={firstNameLabel}
                            value={field.value}
                          />
                        </>
                      )}
                    />
                  </Box>
                  {errors.firstName && (
                    <Text mt={2} sx={styles.errorText}>
                      {errors.firstName.message}
                    </Text>
                  )}
                </Box>
                <Box sx={styles.nameFieldColumn}>
                  <Box sx={styles.nameFieldWrapper}>
                    <FormLabel className="sr-only" htmlFor={NEWSLETTER_FIELD_IDS.lastName}>
                      <FieldLabelContent label={lastNameLabel} required />
                    </FormLabel>
                    <Controller
                      control={control}
                      name="lastName"
                      rules={{
                        required: intl.formatMessage({
                          id: 'newsletter.validation.last_name_required',
                          defaultMessage: 'Last name is required',
                        }),
                        validate: (value) => validateName(value, {
                          maxLength: FIELD_LIMITS.lastName,
                          fieldName: lastNameLabel,
                          formatMessage: intl.formatMessage,
                        }),
                      }}
                      render={({field: {onBlur, onChange, ...field}, fieldState}) => (
                        <>
                          <Input
                            {...field}
                            id={NEWSLETTER_FIELD_IDS.lastName}
                            maxLength={FIELD_LIMITS.lastName}
                            placeholder={appendRequiredAsterisk(lastNameLabel)}
                            sx={styles.input}
                            onBlur={onBlur}
                            onChange={(event) => onChange(sanitizeNameNumbers(event.target.value))}
                            onKeyDown={() => lastNameKeyDownRef.current()}
                          />
                          <FieldValidationStatus
                            enabled
                            errorMessage={fieldState.error?.message}
                            formatMessage={intl.formatMessage}
                            id={`${NEWSLETTER_FIELD_IDS.lastName}-status`}
                            invalid={!!errors.lastName}
                            keyDownRef={lastNameKeyDownRef}
                            label={lastNameLabel}
                            value={field.value}
                          />
                        </>
                      )}
                    />
                  </Box>
                  {errors.lastName && (
                    <Text mt={2} sx={styles.errorText}>
                      {errors.lastName.message}
                    </Text>
                  )}
                </Box>
              </Box>
            )}

            <Box className="newsletter-input-container">
              <HStack spacing={0} sx={styles.inputContainer}>
                {isSuccess ? (
                  <Input
                    readOnly
                    aria-label={emailLabel}
                    sx={styles.input}
                    type="email"
                    value={subscribedEmail}
                  />
                ) : (
                  <>
                    <FormLabel className="sr-only" htmlFor={NEWSLETTER_FIELD_IDS.email}>
                      <FieldLabelContent label={emailLabel} required />
                    </FormLabel>
                    <Controller
                      control={control}
                      name="email"
                      rules={{
                        required: intl.formatMessage({
                          id: 'newsletter.validation.email_required',
                          defaultMessage: 'Email is required',
                        }),
                        validate: (value) => validateEmail(value, {
                          maxLength: FIELD_LIMITS.email,
                          formatMessage: intl.formatMessage,
                        }),
                      }}
                      render={({field: {onBlur, ...field}, fieldState}) => (
                        <>
                          <Input
                            {...field}
                            id={NEWSLETTER_FIELD_IDS.email}
                            maxLength={FIELD_LIMITS.email}
                            placeholder={appendRequiredAsterisk(intl.formatMessage({
                              id: 'newsletter.placeholder.email',
                              defaultMessage: 'Enter your email address',
                            }))}
                            sx={styles.input}
                            type="email"
                            onBlur={onBlur}
                            onKeyDown={() => emailKeyDownRef.current()}
                          />
                          <FieldValidationStatus
                            enabled
                            errorMessage={fieldState.error?.message}
                            formatMessage={intl.formatMessage}
                            id={`${NEWSLETTER_FIELD_IDS.email}-status`}
                            invalid={!!errors.email}
                            keyDownRef={emailKeyDownRef}
                            label={emailLabel}
                            value={field.value}
                          />
                        </>
                      )}
                    />
                  </>
                )}
                {isSuccess ? (
                  <Box aria-hidden="true" sx={styles.submitButton}>
                    <CheckIcon {...styles.submitButtonIcon} />
                  </Box>
                ) : (
                  <IconButton
                    isLoading={isSubmitting}
                    sx={styles.submitButton}
                    type="submit"
                    variant="unstyled"
                    aria-label={intl.formatMessage({
                      id: 'newsletter.button.subscribe_aria',
                      defaultMessage: 'Subscribe to newsletter',
                    })}
                  >
                    <ArrowRightIcon {...styles.submitButtonIcon} />
                  </IconButton>
                )}
              </HStack>
              {!isSuccess && errors.email && (
                <Text mt={2} sx={styles.errorText}>
                  {errors.email.message}
                </Text>
              )}
            </Box>
          </Box>

          {!isSuccess && !subscriptionStatus.isError && (
            <Text sx={styles.recaptcha}>
              <FormattedMessage
                defaultMessage="This site is protected by reCAPTCHA and the {privacyPolicy} and {termsOfService} apply."
                id="newsletter.recaptcha.disclaimer"
                values={{
                  privacyPolicy: (
                    <Text
                      as="a"
                      href="https://policies.google.com/privacy"
                      rel="noopener noreferrer"
                      sx={styles.link}
                      target="_blank"
                    >
                      <FormattedMessage
                        defaultMessage="Privacy Policy"
                        id="newsletter.recaptcha.privacy_policy"
                      />
                    </Text>
                  ),
                  termsOfService: (
                    <Text
                      as="a"
                      href="https://policies.google.com/terms"
                      rel="noopener noreferrer"
                      sx={styles.link}
                      target="_blank"
                    >
                      <FormattedMessage
                        defaultMessage="Terms of Service"
                        id="newsletter.recaptcha.terms_of_service"
                      />
                    </Text>
                  ),
                }}
              />
            </Text>
          )}

          {subscriptionStatus.isSuccess && (
            <Text sx={styles.recaptcha}>
              <FormattedMessage
                defaultMessage="Thanks! You've subscribed."
                id="newsletter.message.success"
              />
            </Text>
          )}

          {subscriptionStatus.isError && (
            <RecaptchaErrorMessage
              message={
                subscriptionStatus.errorMessage ||
                intl.formatMessage({
                  defaultMessage: 'Something Went Wrong!',
                  id: 'newsletter.message.error',
                })
              }
            />
          )}
        </VStack>

        <VStack align="stretch" spacing={4}>
          <Text sx={styles.socialHeading}>
            <FormattedMessage
              defaultMessage="Follow us for daily updates"
              id="newsletter.social.heading"
            />
          </Text>

          <SocialIcons
            pinterestInnerColor="black"
            socialLinks={socialLinks}
            variant="flex-start"
            {...styles.socialIcons}
          />
        </VStack>
      </VStack>
    </Box>
  );
};

NewsletterBase.displayName = 'Newsletter';

// Wrap in the reCAPTCHA provider so the footer subscription can mint/verify a
// token at submit time. No-op when reCAPTCHA is disabled/unconfigured — the
// provider renders children untouched.
export const Newsletter = withRecaptcha(NewsletterBase);

export default Newsletter;
